import { CurrencyType } from '@shared/enums/CurrencyType';
import { CustomEventFieldType } from '@shared/enums/CustomEventFieldType';
import { EventPublishType } from '@shared/enums/EventPublishType';
import { EventType } from '@shared/enums/EventType';
import { FreeTextFormat } from '@shared/enums/FreeTextFormat';
import { PaymentProviderType } from '@shared/enums/PaymentProviderType';
import { IEventContactModel } from '@shared/models/contact.model';
import { ILanguage, ITranslation } from '@shared/models/language.model';
import { IOwnerModel } from '@shared/models/owner.model';
import { ITagModel } from '@shared/models/tag.model';
import { IVenueModel } from '@shared/models/venue.model';
import { ActivityTimeRange, IActivityLogBase } from '@shared/services/activity-service/activity.models';
import { ICustomEventFieldOption, IFieldTranslations } from '../eventcustomfields-service/eventcustomfields-model';
import { IAdvancedTemplateFooterModel } from '../advanced-templates-service/advanced-templates.model';
import { IContentModel } from '@shared/models/content.model';
import { IMultiVenueConfigurationModel } from '@shared/models/multivenue.model';

export interface IEvent {
    id: string;
    name: string;
    description: string;
    url: string;
    image: string;
    cost: number;
    displayOnDashboard: boolean;
    allowSync: boolean;
    isPaid: boolean;
    customIdentifier: string;
    eventType: EventType;
    currencyType: CurrencyType;
    primaryOwner: IOwnerModel;
    eventContact: IEventContactModel;
    paymentSetting: IPaymentSetting;
    allowedListOfInvitees: IAllowedListOfInvitees;
    facilities: string[];
    tags: ITagModel[];
    languages: ILanguage[];
    translations: ITranslation[];
    locationDateTimes: IVenueModel[];
    locationDateTimeIdsToDelete: string[];
    isDraft: boolean;
    isActive: boolean;
    isGdprProcessed: boolean;
    isDeleted: boolean;
    bannerContents: IContentModel[];
    publicPages: ISelectedPublicPage[];
    eventCustomFields: IEventCustomField[];
    footers: IAdvancedTemplateFooterModel[];
    advancedTemplateId: string;
    usesFieldDrive: boolean;
    lineUprSyncEnabled: boolean;
    hasAttendees: boolean;
    multiVenueConfiguration: IMultiVenueConfigurationModel;
}

export class Event implements IEvent {
    constructor(
        public id: string,
        public name: string,
        public description: string,
        public url: string,
        public image: string,
        public cost: number,
        public displayOnDashboard: boolean,
        public allowSync: boolean,
        public isPaid: boolean,
        public customIdentifier: string,
        public eventType: EventType,
        public currencyType: CurrencyType,
        public primaryOwner: IOwnerModel,
        public eventContact: IEventContactModel,
        public paymentSetting: IPaymentSetting,
        public allowedListOfInvitees: IAllowedListOfInvitees,
        public facilities: string[],
        public tags: ITagModel[],
        public languages: ILanguage[],
        public translations: ITranslation[],
        public locationDateTimes: IVenueModel[],
        public locationDateTimeIdsToDelete: string[],
        public isDraft: boolean,
        public isActive: boolean,
        public isGdprProcessed: boolean,
        public isDeleted: boolean,
        public bannerContents: IContentModel[],
        public publicPages: ISelectedPublicPage[],
        public eventCustomFields: IEventCustomField[],
        public footers: IAdvancedTemplateFooterModel[],
        public advancedTemplateId: string,
        public usesFieldDrive: boolean,
        public lineUprSyncEnabled: boolean,
        public hasAttendees: boolean,
        public multiVenueConfiguration: IMultiVenueConfigurationModel
    ) { }
}

export interface IAllowedListOfInvitees {
    id: string;
    name: string;
}

export class AllowedListOfInvitees implements IAllowedListOfInvitees {
    constructor(
        public id: string,
        public name: string
    ) { }
}

export interface IPaymentSetting {
    reference: string;
    currencyType: CurrencyType;
    paymentProviderType: PaymentProviderType;
    allowRefund: boolean;
}

export class PaymentSetting implements IPaymentSetting {
    constructor(
        public reference: string,
        public currencyType: CurrencyType,
        public paymentProviderType: PaymentProviderType,
        public allowRefund: boolean
    ) { }
}

export interface ISelectedPublicPage {
    id: string;
    name: string;
    allowPreRegistration: boolean;
    /**
     * Allows an event to override how a given event instance should be published onto the public page.
     * Explanation: A public page also uses a property 'eventPublishType', that specifies how events, by default, become published.
     * A given event instance can override the default settings.
     */
    eventPublishType: EventPublishType;
}

export class ActivityFilter {
    constructor(public timeRange: ActivityTimeRange, public category: ActivityCategory = null, public userId: string = null, public eventId= null) { }
}

export class ActivityDetailFilter {
    constructor(public timeRange: ActivityTimeRange, public userId: string = null) { }
}

export enum ActivityCategory {
    Created = 0,
    Updated = 1,
    Deleted = 2,
    Export = 3
}

export interface IActivityLog  extends IActivityLogBase {
    eventName: string;
    isCloneOf: string;
    reportType: ReportType;
}

export enum ReportType {
    Details = 0,
    Summary = 1
}

export interface IActivities {
    activities: IActivityLog[];
}

export interface IActivityLogs {
    activities: IActivityLog[];
}

export interface IEventCustomField {
    translations: IFieldTranslations[];
    id: string;
    eventCustomFieldOptions: ICustomEventFieldOption[];
    booleanValue: boolean;
    textValue: string;
    numberValue: any;
    dateTimeValue: any;
    customFieldType: CustomEventFieldType;
    freeTextFormat: FreeTextFormat;
}

export interface IEventDesign {
    eventId: string;
    advancedTemplateId: string;
    bannerContents: IContentModel[];
    footers: IAdvancedTemplateFooterModel[];
}

export class EventDesign implements IEventDesign {
    constructor(
        public eventId: string,
        public advancedTemplateId: string,
        public bannerContents: IContentModel[],
        public footers: IAdvancedTemplateFooterModel[]
    ) { }
}

export interface IEventForDropdown {
    id: string;
    text: string;
}

export class EventForDropdown implements IEventForDropdown {
    constructor(
        public id: string,
        public text: string
    ) { }
}

export interface IEventsForDropdown {
    events: IEventForDropdown[];
}
